"use client";

import { parseDate } from "@internationalized/date";
import { useEffect, useMemo, useState } from "react";
import type { DateValue } from "react-aria";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import {
  addFilterCondition,
  createEmptyFilter,
  type FacetConfig,
  type FacetCounts,
  type FilterState,
  getFilterFields,
  isFilterEmpty,
  removeFilterCondition,
} from "../../types/DataControls";
import { Button } from "../Button";
import { Checkbox, CheckboxGroup } from "../Checkbox";
import { Collapse, CollapseContent, CollapseHeader, CollapseItem } from "../Collapse";
import { DateRangePicker } from "../DateRangePicker";
import { Dialog } from "../Dialog";
import { Icon } from "../Icon";
import { NumberField } from "../NumberField";
import { Switch } from "../Switch";
import { SearchControl } from "./SearchControl";

/**
 * Format a number with thousands separators
 */
function formatCount(count: number): string {
  return new Intl.NumberFormat("en-US").format(count);
}

export interface FilterDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Facet configurations defining available filters */
  facetConfigs: FacetConfig[];
  /** Optional facet counts to display next to options */
  facetCounts?: FacetCounts;
  /** Current filter state */
  currentFilters: FilterState;
  /** Callback when filters are applied */
  onApply: (filters: FilterState) => void;
  /** Optional callback to clear all filters */
  onClear?: () => void;
  /** Dialog title */
  title?: string;
  /** Optional: Show result count in footer (number of items that match current draft filters) */
  resultCount?: number;
}

/**
 * FilterDialog
 *
 * A reusable dialog for applying structured filters to data.
 * Automatically renders appropriate UI based on facet type and configuration.
 *
 * **Supported Facet Types:**
 * - `string`: Checkbox group for multi-select with search
 * - `number`: Min/max range inputs
 * - `boolean`: Switch toggle (Yes/No)
 * - `date`: Date range picker
 *
 * @example
 * ```tsx
 * <FilterDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   facetConfigs={[
 *     { field: 'department', label: 'Department', type: 'string' },
 *     { field: 'salary', label: 'Salary', type: 'number' },
 *     { field: 'isActive', label: 'Active', type: 'boolean' },
 *     { field: 'startDate', label: 'Start Date', type: 'date' }
 *   ]}
 *   facetCounts={{
 *     department: { Engineering: 10, Sales: 5 }
 *   }}
 *   currentFilters={filters}
 *   onApply={setFilters}
 * />
 * ```
 */
export function FilterDialog({
  isOpen,
  onClose,
  facetConfigs,
  facetCounts = {},
  currentFilters,
  onApply,
  onClear,
  title = "Filters",
  resultCount,
}: FilterDialogProps) {
  // Local state for building filters before applying
  const [draftFilters, setDraftFilters] = useState<FilterState>(currentFilters);
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [expandedOptions, setExpandedOptions] = useState<Record<string, boolean>>({});
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  // Responsive breakpoint detection
  const { isMobile } = useBreakpoint();

  // Check if any facets have groups defined
  const hasGroups = useMemo(() => facetConfigs.some((config) => config.group), [facetConfigs]);

  // Get unique groups in order they appear, plus ungrouped facets
  const { groups, ungroupedFacets, facetsByGroup } = useMemo(() => {
    const groupSet = new Set<string>();
    const ungrouped: FacetConfig[] = [];
    const byGroup: Record<string, FacetConfig[]> = {};

    for (const config of facetConfigs) {
      if (config.group) {
        if (!groupSet.has(config.group)) {
          groupSet.add(config.group);
          byGroup[config.group] = [];
        }
        byGroup[config.group].push(config);
      } else {
        ungrouped.push(config);
      }
    }

    return {
      groups: Array.from(groupSet),
      ungroupedFacets: ungrouped,
      facetsByGroup: byGroup,
    };
  }, [facetConfigs]);

  // Reset draft when dialog opens
  useEffect(() => {
    if (isOpen) {
      setDraftFilters(currentFilters);
      setSearchTerms({});
      setExpandedOptions({});
      // Default to first group if groups exist (desktop only - mobile shows list first)
      setSelectedGroup(!isMobile && groups.length > 0 ? groups[0] : null);
    }
  }, [isOpen, currentFilters, groups, isMobile]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    if (!draftFilters || isFilterEmpty(draftFilters)) return 0;
    return getFilterFields(draftFilters).length;
  }, [draftFilters]);

  // Apply filters and close
  const handleApply = () => {
    onApply(draftFilters);
    onClose();
  };

  // Clear all filters
  const handleClear = () => {
    const emptyFilters = createEmptyFilter();
    setDraftFilters(emptyFilters);
    if (onClear) {
      onClear();
    } else {
      onApply(emptyFilters);
    }
    onClose();
  };

  // Get selected values for a field
  const getSelectedValues = (field: string): string[] => {
    if (!draftFilters) return [];

    const activeFields = getFilterFields(draftFilters);
    if (!activeFields.includes(field)) return [];

    // Find conditions for this field
    const values: string[] = [];
    function traverse(filters: FilterState) {
      if (!filters) return;
      for (const condition of filters.conditions) {
        if ("conditions" in condition) {
          traverse(condition);
        } else if (condition.field === field && condition.operator === "in") {
          if (Array.isArray(condition.value)) {
            values.push(...condition.value.map(String));
          }
        }
      }
    }
    traverse(draftFilters);
    return values;
  };

  // Set min/max range for number filters
  const setNumberRange = (field: string, min?: number, max?: number) => {
    let updatedFilters = removeFilterCondition(draftFilters, field);

    if (min !== undefined) {
      updatedFilters = addFilterCondition(updatedFilters, {
        field,
        operator: "gte",
        value: min,
      });
    }

    if (max !== undefined) {
      updatedFilters = addFilterCondition(updatedFilters, {
        field,
        operator: "lte",
        value: max,
      });
    }

    setDraftFilters(updatedFilters);
  };

  // Get boolean value for a field
  const getBooleanValue = (field: string): boolean | undefined => {
    if (!draftFilters) return undefined;

    let value: boolean | undefined;
    function traverse(filters: FilterState) {
      if (!filters) return;
      for (const condition of filters.conditions) {
        if ("conditions" in condition) {
          traverse(condition);
        } else if (condition.field === field && condition.operator === "eq") {
          if (typeof condition.value === "boolean") {
            value = condition.value;
          }
        }
      }
    }
    traverse(draftFilters);
    return value;
  };

  // Set boolean filter
  const setBooleanValue = (field: string, value: boolean | undefined) => {
    let updatedFilters = removeFilterCondition(draftFilters, field);

    if (value !== undefined) {
      updatedFilters = addFilterCondition(updatedFilters, {
        field,
        operator: "eq",
        value,
      });
    }

    setDraftFilters(updatedFilters);
  };

  // Set date range filter
  const setDateRange = (field: string, start?: DateValue, end?: DateValue) => {
    let updatedFilters = removeFilterCondition(draftFilters, field);

    if (start) {
      updatedFilters = addFilterCondition(updatedFilters, {
        field,
        operator: "gte",
        value: start.toString(),
      });
    }

    if (end) {
      updatedFilters = addFilterCondition(updatedFilters, {
        field,
        operator: "lte",
        value: end.toString(),
      });
    }

    setDraftFilters(updatedFilters);
  };

  // Count active filters per group
  const getGroupActiveCount = (groupName: string): number => {
    const groupFacets = facetsByGroup[groupName] || [];
    const activeFields = draftFilters ? getFilterFields(draftFilters) : [];
    return groupFacets.filter((f) => activeFields.includes(f.field)).length;
  };

  // Shared styles
  // Dialog header uses px-6, plus icon button has p-1 padding, plus extra for visual balance
  const COLLAPSE_HEADER_CLASS = "pr-9 bg-background-muted/50";
  const COLLAPSE_CONTENT_CLASS = "border-t border-border-muted";
  const STATUS_BADGE_CLASS = "text-sm font-medium text-brand-primary";

  // Helper to render status badge
  const renderStatusBadge = (content: React.ReactNode) => <span className={STATUS_BADGE_CLASS}>{content}</span>;

  // Render facet UI based on type
  const renderFacet = (config: FacetConfig) => {
    const counts = facetCounts[config.field] || {};
    const searchTerm = searchTerms[config.field] || "";

    // Get available values
    let availableValues: Array<{
      value: string;
      label: string;
      count?: number;
    }> = [];

    if (config.values) {
      // Use predefined values
      availableValues = config.values.map((v) => ({
        value: String(v.value),
        label: v.label,
        count: counts[String(v.value)],
      }));
    } else {
      // Use values from facet counts
      availableValues = Object.entries(counts).map(([value, count]) => ({
        value,
        label: value,
        count,
      }));
    }

    // Store original count before filtering
    const totalAvailableCount = availableValues.length;

    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      availableValues = availableValues.filter((v) => v.label.toLowerCase().includes(lowerSearch));
    }

    // Sort by count (descending) then label
    availableValues.sort((a, b) => {
      if (a.count !== undefined && b.count !== undefined) {
        if (b.count !== a.count) return b.count - a.count;
      }
      return a.label.localeCompare(b.label);
    });

    const type = config.type || "string";

    if (type === "string" || !config.type) {
      const selectedValues = getSelectedValues(config.field);
      const searchThreshold = config.searchThreshold ?? 10;
      const maxVisible = config.maxVisibleOptions ?? 50;
      const isExpanded = expandedOptions[config.field] ?? false;

      // Determine visible options based on expansion state
      const visibleValues =
        isExpanded || availableValues.length <= maxVisible ? availableValues : availableValues.slice(0, maxVisible);
      const hasMore = availableValues.length > maxVisible && !isExpanded;

      return (
        <CollapseItem key={config.field} id={config.field}>
          <CollapseHeader
            title={config.label}
            description={config.description}
            className={COLLAPSE_HEADER_CLASS}
            endContent={selectedValues.length > 0 ? renderStatusBadge(`${selectedValues.length} selected`) : undefined}
          />
          <CollapseContent className={COLLAPSE_CONTENT_CLASS}>
            <div className="space-y-4 pt-4 pb-4">
              {/* Search within facet if above threshold */}
              {totalAvailableCount > searchThreshold && (
                <SearchControl
                  value={searchTerm}
                  onChange={(value) =>
                    setSearchTerms((prev) => ({
                      ...prev,
                      [config.field]: value,
                    }))
                  }
                  onClear={() => setSearchTerms((prev) => ({ ...prev, [config.field]: "" }))}
                  placeholder={`Search ${config.label.toLowerCase()}...`}
                  size="sm"
                  className="w-full !max-w-none pr-4"
                />
              )}

              {/* Checkbox list - NO NESTED SCROLL */}
              <CheckboxGroup
                value={selectedValues}
                onChange={(values) => {
                  // Update filter with new selected values
                  let updatedFilters = removeFilterCondition(draftFilters, config.field);
                  if (values.length > 0) {
                    updatedFilters = addFilterCondition(updatedFilters, {
                      field: config.field,
                      operator: "in",
                      value: values,
                    });
                  }
                  setDraftFilters(updatedFilters);
                }}
              >
                <div className="space-y-1.5">
                  {visibleValues.map((option) => (
                    <div
                      key={option.value}
                      className="rounded-md py-2 pr-4 transition-colors hover:bg-background-hover"
                    >
                      <Checkbox value={option.value}>
                        <div className="flex items-center justify-between gap-3 flex-1">
                          {config.renderLabel ? (
                            config.renderLabel(option.value, option.label)
                          ) : (
                            <span className="text-sm font-medium text-text-body">{option.label}</span>
                          )}
                          {option.count !== undefined && (
                            <span className="text-xs font-medium text-text-muted tabular-nums">
                              {formatCount(option.count)}
                            </span>
                          )}
                        </div>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </CheckboxGroup>

              {/* Show all / Show less button */}
              {hasMore && (
                <div className="pt-2 border-t border-border-muted">
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() =>
                      setExpandedOptions((prev) => ({
                        ...prev,
                        [config.field]: true,
                      }))
                    }
                    className="w-full"
                  >
                    Show all {availableValues.length} options
                  </Button>
                </div>
              )}
              {isExpanded && availableValues.length > maxVisible && (
                <div className="pt-2 border-t border-border-muted">
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() =>
                      setExpandedOptions((prev) => ({
                        ...prev,
                        [config.field]: false,
                      }))
                    }
                    className="w-full"
                  >
                    Show less
                  </Button>
                </div>
              )}
            </div>
          </CollapseContent>
        </CollapseItem>
      );
    }

    if (type === "number") {
      // Extract current min/max from filters
      let currentMin: number | undefined;
      let currentMax: number | undefined;

      const findRange = (filters: FilterState) => {
        if (!filters) return;
        for (const condition of filters.conditions) {
          if ("conditions" in condition) {
            findRange(condition);
          } else if (condition.field === config.field) {
            if (condition.operator === "gte" && typeof condition.value === "number") {
              currentMin = condition.value;
            }
            if (condition.operator === "lte" && typeof condition.value === "number") {
              currentMax = condition.value;
            }
          }
        }
      };
      findRange(draftFilters);

      const hasValue = currentMin !== undefined || currentMax !== undefined;

      return (
        <CollapseItem key={config.field} id={config.field}>
          <CollapseHeader
            title={config.label}
            description={config.description}
            className={COLLAPSE_HEADER_CLASS}
            endContent={
              hasValue
                ? renderStatusBadge(
                    currentMin !== undefined && currentMax !== undefined
                      ? `${currentMin} - ${currentMax}`
                      : currentMin !== undefined
                        ? `≥ ${currentMin}`
                        : `≤ ${currentMax}`
                  )
                : undefined
            }
          />
          <CollapseContent className={COLLAPSE_CONTENT_CLASS}>
            <div className="grid grid-cols-2 gap-4 pt-4 pb-4">
              <NumberField
                key={`${config.field}-min`}
                label="Min"
                value={currentMin}
                onChange={(value) => setNumberRange(config.field, value, currentMax)}
              />
              <NumberField
                key={`${config.field}-max`}
                label="Max"
                value={currentMax}
                onChange={(value) => setNumberRange(config.field, currentMin, value)}
              />
            </div>
          </CollapseContent>
        </CollapseItem>
      );
    }

    if (type === "boolean") {
      const currentValue = getBooleanValue(config.field);

      return (
        <CollapseItem key={config.field} id={config.field}>
          <CollapseHeader
            title={config.label}
            description={config.description}
            className={COLLAPSE_HEADER_CLASS}
            endContent={currentValue !== undefined ? renderStatusBadge(currentValue ? "Yes" : "No") : undefined}
          />
          <CollapseContent className={COLLAPSE_CONTENT_CLASS}>
            <div className="flex items-center justify-between pt-4 pb-4">
              <span className="text-sm font-medium text-text-body">
                {currentValue !== undefined ? (currentValue ? "Enabled" : "Disabled") : "No filter applied"}
              </span>
              <Switch
                size="lg"
                isSelected={currentValue ?? false}
                onChange={(isSelected) => setBooleanValue(config.field, isSelected ? true : undefined)}
              >
                {currentValue ? "Yes" : "No"}
              </Switch>
            </div>
          </CollapseContent>
        </CollapseItem>
      );
    }

    if (type === "date") {
      // Extract current date range from filters using recursive traversal
      let currentStart: string | undefined;
      let currentEnd: string | undefined;

      const findDateRange = (filters: FilterState) => {
        if (!filters) return;
        for (const condition of filters.conditions) {
          if ("conditions" in condition) {
            findDateRange(condition);
          } else if (condition.field === config.field) {
            if (condition.operator === "gte") currentStart = String(condition.value);
            if (condition.operator === "lte") currentEnd = String(condition.value);
          }
        }
      };
      findDateRange(draftFilters);

      // Check if date filter is active
      const hasDateFilter = currentStart !== undefined || currentEnd !== undefined;

      // Helper to safely parse date string
      const safeParseDateString = (dateStr: string | undefined): ReturnType<typeof parseDate> | undefined => {
        if (!dateStr) return undefined;
        try {
          // Check if it's a valid ISO date format (YYYY-MM-DD)
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            return parseDate(dateStr);
          }
          // If it's not ISO format, return undefined
          return undefined;
        } catch {
          return undefined;
        }
      };

      const parsedStart = safeParseDateString(currentStart);
      const parsedEnd = safeParseDateString(currentEnd);

      return (
        <CollapseItem key={config.field} id={config.field}>
          <CollapseHeader
            title={config.label}
            description={config.description}
            className={COLLAPSE_HEADER_CLASS}
            endContent={hasDateFilter ? renderStatusBadge("Date range selected") : undefined}
          />
          <CollapseContent className={COLLAPSE_CONTENT_CLASS}>
            <div className="pt-4 pb-4">
              <DateRangePicker
                label="Date Range"
                value={
                  parsedStart && parsedEnd
                    ? {
                        start: parsedStart,
                        end: parsedEnd,
                      }
                    : undefined
                }
                onChange={(range) => {
                  if (range) {
                    setDateRange(config.field, range.start, range.end);
                  } else {
                    setDateRange(config.field, undefined, undefined);
                  }
                }}
              />
            </div>
          </CollapseContent>
        </CollapseItem>
      );
    }

    return null;
  };

  // Render the group list for mobile drill-down or desktop sidebar
  const renderGroupList = () => (
    <div className="flex flex-col">
      {/* Ungrouped facets first */}
      {ungroupedFacets.map((config) => {
        const activeFields = draftFilters ? getFilterFields(draftFilters) : [];
        const isActive = activeFields.includes(config.field);
        return (
          <button
            key={config.field}
            type="button"
            onClick={() => setSelectedGroup(`__ungrouped__${config.field}`)}
            className="flex items-center justify-between px-6 py-3 text-left hover:bg-background-hover border-b border-border-muted transition-colors"
          >
            <span className="text-sm font-medium text-text-body">{config.label}</span>
            <div className="flex items-center gap-2">
              {isActive && <span className={STATUS_BADGE_CLASS}>1 active</span>}
              <Icon name="CaretRight" size="sm" className="text-text-muted" />
            </div>
          </button>
        );
      })}
      {/* Grouped facets */}
      {groups.map((groupName) => {
        const activeCount = getGroupActiveCount(groupName);
        return (
          <button
            key={groupName}
            type="button"
            onClick={() => setSelectedGroup(groupName)}
            className="flex items-center justify-between px-6 py-3 text-left hover:bg-background-hover border-b border-border-muted transition-colors"
          >
            <span className="text-sm font-medium text-text-body">{groupName}</span>
            <div className="flex items-center gap-2">
              {activeCount > 0 && <span className={STATUS_BADGE_CLASS}>{activeCount} active</span>}
              <Icon name="CaretRight" size="sm" className="text-text-muted" />
            </div>
          </button>
        );
      })}
    </div>
  );

  // Render facets for a selected group
  const renderGroupFacets = (groupName: string) => {
    // Handle ungrouped single facets
    if (groupName.startsWith("__ungrouped__")) {
      const fieldName = groupName.replace("__ungrouped__", "");
      const config = ungroupedFacets.find((f) => f.field === fieldName);
      if (!config) return null;
      return (
        <Collapse variant="contained" density="comfortable" showDividers={true} defaultExpandedKeys={[config.field]}>
          {renderFacet(config)}
        </Collapse>
      );
    }

    const groupFacets = facetsByGroup[groupName] || [];
    if (groupFacets.length === 0) return null;

    return (
      <Collapse
        variant="contained"
        density="comfortable"
        showDividers={true}
        defaultExpandedKeys={[groupFacets[0].field]}
      >
        {groupFacets.map((config) => renderFacet(config))}
      </Collapse>
    );
  };

  // Render mobile drill-down view
  const renderMobileGrouped = () => {
    if (selectedGroup) {
      const displayName = selectedGroup.startsWith("__ungrouped__")
        ? ungroupedFacets.find((f) => f.field === selectedGroup.replace("__ungrouped__", ""))?.label || selectedGroup
        : selectedGroup;

      return (
        <div className="flex flex-col h-full">
          {/* Back button header */}
          <button
            type="button"
            onClick={() => setSelectedGroup(null)}
            className="flex items-center gap-2 px-4 py-3 border-b border-border-muted hover:bg-background-hover transition-colors"
          >
            <Icon name="CaretLeft" size="sm" className="text-text-muted" />
            <span className="text-sm font-medium text-text-body">{displayName}</span>
          </button>
          {/* Group facets */}
          <div className="flex-1 overflow-y-auto">{renderGroupFacets(selectedGroup)}</div>
        </div>
      );
    }

    return renderGroupList();
  };

  // Render desktop two-panel view
  const renderDesktopGrouped = () => (
    <div className="flex h-full">
      {/* Left panel - group list */}
      <div className="w-48 flex-shrink-0 border-r border-border-muted overflow-y-auto bg-background-muted/30">
        {/* Ungrouped facets */}
        {ungroupedFacets.map((config) => {
          const activeFields = draftFilters ? getFilterFields(draftFilters) : [];
          const isActive = activeFields.includes(config.field);
          const isSelected = selectedGroup === `__ungrouped__${config.field}`;
          return (
            <button
              key={config.field}
              type="button"
              onClick={() => setSelectedGroup(`__ungrouped__${config.field}`)}
              className={`flex items-center justify-between w-full px-4 py-3 text-left border-b border-border-muted transition-colors ${
                isSelected ? "bg-brand-primary/10" : "hover:bg-background-hover"
              }`}
            >
              <span
                className={`text-sm truncate ${isSelected ? "font-semibold text-text-body" : "font-medium text-text-body"}`}
              >
                {config.label}
              </span>
              {isActive && <span className="w-2 h-2 rounded-full bg-brand-primary flex-shrink-0 ml-2" />}
            </button>
          );
        })}
        {/* Grouped facets */}
        {groups.map((groupName) => {
          const activeCount = getGroupActiveCount(groupName);
          const isSelected = selectedGroup === groupName;
          return (
            <button
              key={groupName}
              type="button"
              onClick={() => setSelectedGroup(groupName)}
              className={`flex items-center justify-between w-full px-4 py-3 text-left border-b border-border-muted transition-colors ${
                isSelected ? "bg-brand-primary/10" : "hover:bg-background-hover"
              }`}
            >
              <span
                className={`text-sm truncate ${isSelected ? "font-semibold text-text-body" : "font-medium text-text-body"}`}
              >
                {groupName}
              </span>
              {activeCount > 0 && <span className="w-2 h-2 rounded-full bg-brand-primary flex-shrink-0 ml-2" />}
            </button>
          );
        })}
      </div>
      {/* Right panel - facets for selected group */}
      <div className="flex-1 overflow-y-auto">
        {selectedGroup ? (
          renderGroupFacets(selectedGroup)
        ) : (
          <div className="flex items-center justify-center h-full text-text-muted text-sm">
            Select a category to view filters
          </div>
        )}
      </div>
    </div>
  );

  // Render flat list (original behavior for ungrouped facets)
  const renderFlatList = () => (
    <Collapse
      variant="contained"
      density="comfortable"
      showDividers={true}
      defaultExpandedKeys={[facetConfigs[0].field]}
    >
      {facetConfigs.map((config) => renderFacet(config))}
    </Collapse>
  );

  // Determine dialog title based on mobile drill-down state
  const getDialogTitle = () => {
    if (hasGroups && isMobile && selectedGroup) {
      return selectedGroup.startsWith("__ungrouped__")
        ? ungroupedFacets.find((f) => f.field === selectedGroup.replace("__ungrouped__", ""))?.label || title
        : selectedGroup;
    }
    return activeFilterCount > 0 ? `${title} (${activeFilterCount} active)` : title;
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={getDialogTitle()}
      hideCloseIcon={false}
      primaryAction={{
        label: "Apply filters",
        onPress: handleApply,
        variant: "default",
      }}
      secondaryAction={{
        label: "Clear all",
        onPress: handleClear,
      }}
      maxWidth={hasGroups ? "800px" : "700px"}
      height="600px"
      mobileHeight="85vh"
      contentPadding={false}
      footerContent={
        resultCount !== undefined ? (
          <div className="text-sm text-text-muted text-center">
            <span className="font-semibold text-text-body">{formatCount(resultCount)}</span>{" "}
            {resultCount === 1 ? "result" : "results"} with current filters
          </div>
        ) : undefined
      }
    >
      {/* Filter body */}
      {facetConfigs.length === 0 ? (
        <div className="py-12 text-center">
          <div className="text-sm font-medium text-text-muted">No filters available</div>
        </div>
      ) : hasGroups ? (
        isMobile ? (
          renderMobileGrouped()
        ) : (
          renderDesktopGrouped()
        )
      ) : (
        renderFlatList()
      )}
    </Dialog>
  );
}
